import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  IconButton,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { apiServices } from '../../../../services/apiServices';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../../assets/data/firebase';

const OneToOneChat = ({ selectedUser, onUserSelect, onBackToUserList }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Generate a unique chat ID based on user IDs (sorted to ensure consistency)
  const currentUserId = user?.userid || user?.uid;
  const chatId = selectedUser ? [currentUserId, selectedUser.userid].sort().join("_") : null;

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.peacekeeper_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Listen to messages when chat is selected
  useEffect(() => {
    if (!chatId) return;

    const messagesRef = collection(db, "peerChats", chatId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Using the PeaceKeeper API endpoint from the documentation
      const response = await apiServices.getAPI('users/listing?limit=100');
      if (response.data?.success) {
        setUsers(response.data.data.users);
        setFilteredUsers(response.data.data.users);
      } else {
        // Fallback to sample data if API response format is different
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to sample data if API fails
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Sample data fallback
  const getSampleUsers = () => [
    {
      userid: 1,
      username: "John Doe",
      email: "john@example.com",
      peacekeeper_id: "IN-00000001-PK",
      country: "India",
      activestatus: "Y",
      payment_status: "COMPLETED"
    },
    {
      userid: 2,
      username: "Sarah Wilson",
      email: "sarah@example.com",
      peacekeeper_id: "IN-00000002-PK",
      country: "USA",
      activestatus: "Y",
      payment_status: "COMPLETED"
    },
    {
      userid: 3,
      username: "Michael Chen",
      email: "michael@example.com",
      peacekeeper_id: "IN-00000003-PK",
      country: "Canada",
      activestatus: "Y",
      payment_status: "PENDING"
    },
    {
      userid: 4,
      username: "Emma Rodriguez",
      email: "emma@example.com",
      peacekeeper_id: "IN-00000004-PK",
      country: "Spain",
      activestatus: "Y",
      payment_status: "COMPLETED"
    },
    {
      userid: 5,
      username: "David Kim",
      email: "david@example.com",
      peacekeeper_id: "IN-00000005-PK",
      country: "South Korea",
      activestatus: "Y",
      payment_status: "PENDING"
    },
    {
      userid: 6,
      username: "Lisa Thompson",
      email: "lisa@example.com",
      peacekeeper_id: "IN-00000006-PK",
      country: "Australia",
      activestatus: "Y",
      payment_status: "COMPLETED"
    }
  ];

  const handleUserClick = (selectedUserData) => {
    onUserSelect(selectedUserData);
    setMessages([]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === "" || !chatId) return;

    try {
      const messagesRef = collection(db, "peerChats", chatId, "messages");
      await addDoc(messagesRef, {
        text: newMessage,
        senderId: currentUserId,
        senderEmail: user?.username || user?.email || user?.name || "unknown",
        createdAt: serverTimestamp(),
      });
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message: ", error);
    }
  };

  // Helper function to format date as string
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  };

  // Helper function to format time as string
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  };

  if (selectedUser) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#f8f9fa'
      }}>
        {/* User Title */}
        <Box sx={{ 
          p: 1, 
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
          textAlign: 'center'
        }}>
          <Typography 
            variant="h5" 
            className='green-title'
            sx={{ 
              fontWeight: 'bold',
              fontSize: '1.5rem',
              mb: 0.5
            }}
          >
            {selectedUser.username}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.9rem' }}
          >
            {selectedUser.peacekeeper_id} • {selectedUser.country}
          </Typography>
        </Box>

        {/* Chat Messages Area */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 2,
          pb: '80px', // Add padding to prevent messages from being hidden behind fixed input
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {Object.keys(groupedMessages).map(date => (
            <Box key={date} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Date Separator */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                my: 2 
              }}>
                <Chip 
                  label={date}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(0,0,0,0.08)',
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
              
              {/* Messages for this date */}
              {groupedMessages[date].map(msg => {
                const isCurrentUser = msg.senderId === currentUserId;
                
                return (
                  <Box key={msg.id} sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                    gap: 0.5
                  }}>
                    {/* Sender Info */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 0.5,
                      ml: isCurrentUser ? 0 : 1,
                      mr: isCurrentUser ? 1 : 0
                    }}>
                      <Avatar 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          bgcolor: isCurrentUser ? 'primary.main' : 'secondary.main'
                        }}
                      >
                        {msg.senderEmail.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography 
                        variant="" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        {msg.senderEmail}
                      </Typography>
                    </Box>
                    
                    {/* Message Content */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                      maxWidth: '70%'
                    }}>
                      {/* Text Message */}
                      {msg.text && (
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2,
                            bgcolor: isCurrentUser ? 'primary.main' : 'white',
                            color: isCurrentUser ? 'white' : 'text.primary',
                            borderRadius: 3,
                            borderTopLeftRadius: isCurrentUser ? 3 : 1,
                            borderTopRightRadius: isCurrentUser ? 1 : 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            wordBreak: 'break-word',
                            position: 'relative'
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                            {msg.text}
                          </Typography>
                          
                          {/* Time */}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              mt: 1,
                              opacity: 0.7,
                              fontSize: '0.7rem'
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
        
        {/* Message Input Area - Fixed at Extreme Bottom */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'white',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }}>
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* Message Input Field */}
            <Box sx={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '24px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1976d2';
                  e.target.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Box>
            
            {/* Send Button */}
            <button 
              type="submit" 
              disabled={!newMessage.trim()}
              style={{
                padding: '12px 16px',
                backgroundColor: newMessage.trim() ? '#1976d2' : '#e0e0e0',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                minWidth: '48px'
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </button>
          </form>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Header */}
      <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} display="flex" alignItems="center">
       <Grid size={{ xs: 12, md: 6 }} >
            <Typography variant="body2" color="text.secondary">
          Connect with fellow peacekeepers individually
        </Typography>
        
       
       </Grid>
        <Grid size={{ xs: 12, md: 6 }} >
               <TextField
          fullWidth
          variant="outlined"
          placeholder="Search users by name, email, or peacekeeper ID..."
          value={searchTerm}
          size='small'
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
        </Grid>
      </Grid>
        
      
      </Box>

      {/* Users List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredUsers.map((user, index) => (
            <Box key={user.userid}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  },
                  borderBottom: index < filteredUsers.length - 1 ? 1 : 0,
                  borderColor: 'divider'
                }}
                onClick={() => handleUserClick(user)}
              >
                {/* User Avatar */}
                <Avatar 
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    mr: 1,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.2rem'
                  }}
                >
                  {getInitials(user.username)}
                </Avatar>
                
                {/* User Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography 
                      variant="" 
                      fontWeight="800" 
                      sx={{ 
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {user.username}
                    </Typography>
                    
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      color="primary" 
                      fontWeight="bold"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {user.peacekeeper_id}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {user.country}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Chat Icon */}
                <Box sx={{ ml: 2, color: 'action.active' }}>
                  <ChatIcon sx={{ fontSize: 20 }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!loading && filteredUsers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search terms
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OneToOneChat;
